import { Check, ChevronDown } from 'lucide-react';
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { Domain } from '../../types';
import { domainService } from '../../services/domainService';

interface DomainSelectorProps {
  selectedDomainId: string | null;
  onDomainChange: (domain: Domain | null) => void;
  className?: string;
}

export const DomainSelector: React.FC<DomainSelectorProps> = ({
  selectedDomainId,
  onDomainChange,
  className = '',
}) => {
  const { t } = useTranslation();
  const [domains, setDomains] = useState<Domain[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    loadDomains();
  }, []);

  const loadDomains = async () => {
    setIsLoading(true);
    try {
      const userDomains = await domainService.getUserDomains();
      setDomains(userDomains);
    } catch (error) {
      console.error('Error loading domains:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const selectedDomain = domains.find(w => w.id === selectedDomainId);

  return (
    <div className={`relative inline-block text-left ${className}`}>
      <div>
        <button
          type="button"
          className="inline-flex w-full justify-between items-center rounded-md border border-input bg-background px-3 py-2 text-sm font-medium text-foreground shadow-sm hover:bg-accent focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:ring-offset-background"
          onClick={() => setIsOpen(!isOpen)}
          disabled={isLoading}
        >
          {isLoading ? (
            <span className="truncate">{t('selectors.loading')}</span>
          ) : selectedDomain ? (
            <span className="truncate" title={selectedDomain.name}>{selectedDomain.name}</span>
          ) : (
            <span className="truncate text-muted-foreground">{t('selectors.allDomains')}</span>
          )}
          <ChevronDown className="ml-2 h-5 w-5 shrink-0" />
        </button>
      </div>

      {isOpen && (
        <div className="absolute left-0 z-10 mt-2 min-w-full w-72 max-w-[calc(100vw-2rem)] origin-top-left rounded-md bg-card border border-border shadow-lg focus:outline-none">
          <div className="py-1">
            {/* Option "All domains" */}
            <button
              className="group flex w-full items-center px-4 py-2 text-sm text-foreground hover:bg-accent"
              onClick={() => {
                onDomainChange(null);
                setIsOpen(false);
              }}
            >
              <div className="flex items-center justify-between w-full">
                <span>{t('selectors.allDomains')}</span>
                {!selectedDomainId && (
                  <Check className="h-4 w-4 text-primary" />
                )}
              </div>
            </button>

            <div className="border-t border-border" />

            {domains.map((domain) => (
              <button
                key={domain.id}
                className="group flex w-full items-center px-4 py-2 text-sm text-foreground hover:bg-accent"
                onClick={() => {
                  onDomainChange(domain);
                  setIsOpen(false);
                }}
              >
                <div className="flex items-center justify-between gap-2 w-full">
                  <div className="flex-1 min-w-0 text-left">
                    <div className="font-medium break-words">{domain.name}</div>
                    {domain.description && (
                      <div className="text-xs text-muted-foreground truncate">
                        {domain.description}
                      </div>
                    )}
                  </div>
                  {selectedDomainId === domain.id && (
                    <Check className="h-4 w-4 text-primary shrink-0" />
                  )}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Overlay to close dropdown when clicking outside */}
      {isOpen && (
        <div
          className="fixed inset-0 z-0"
          onClick={() => setIsOpen(false)}
        />
      )}
    </div>
  );
};
